import { rem } from "@certn/cascadia";
import {
  fontFamilies,
  lineHeights,
  shapes,
  sizes,
  spacing,
  weights,
} from "@certn/cascadia-tokens";
import { Global, css } from "@emotion/react";
import { CSSProperties, PropsWithChildren, useMemo } from "react";

import { useShadows } from "../components/utils";
import { useColors } from "../hooks/useColors";
import { usePresetStyles } from "../hooks/usePresetSyles";

function convertTokensToVariables<T>(
  tokens: Record<string, T>,
  formatValue?: (value: T) => string
) {
  return Object.entries(tokens).reduce<CSSProperties>((curr, [key, value]) => {
    return {
      ...curr,
      [`--${key}`]: formatValue ? formatValue(value) : value,
    };
  }, {});
}

export function GlobalStyles(props: PropsWithChildren) {
  const colors = useColors();
  const shadows = useShadows();
  const presetStyles = usePresetStyles();

  const variables = useMemo(() => {
    return {
      ...convertTokensToVariables(colors),
      ...convertTokensToVariables(shadows),
      ...convertTokensToVariables(presetStyles),
    };
  }, [colors, presetStyles, shadows]);

  return (
    <>
      <Global
        styles={css(
          {
            body: variables,
          },
          styles.global
        )}
      />
      {props.children}
    </>
  );
}

const styles = {
  global: css({
    body: {
      minHeight: "100vh",
      background: "var(--surface-base)",
      color: "var(--text-base)",
      ...convertTokensToVariables(spacing, rem),
      ...convertTokensToVariables(shapes, (value) => `${value}px`),
      ...convertTokensToVariables(fontFamilies),
      ...convertTokensToVariables(sizes, rem),
      ...convertTokensToVariables(lineHeights, rem),
      ...convertTokensToVariables(weights),
      fontFamily: "var(--font-family-default)",
      scrollbarColor:
        "var(--background-selected-medium) var(--background-ghost-hover)",
    },
  }),
};
